import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import './nashi-messendzhery.css';
import { IconChevronUp, IconCopy, IconMessage } from '@tabler/icons-react';
import { useText } from '../../clients/words';
import { otpravitSsylkuSms, type NashMessendzher } from '../api';
import { ZnachokKanala } from './ZnachokKanala';
import { usePermissions } from '../../auth/usePermissions';

/** s924: the public account belongs to our line; copy never creates a conversation. */
export function NashiMessendzhery({ inquiryId, rows, onSms }: {
  inquiryId?: number;
  rows: NashMessendzher[];
  onSms?: (row: NashMessendzher, intent: string) => Promise<{ ok: boolean }>;
}) {
  const text = useText();
  const permissions = usePermissions();
  const [open, setOpen] = useState(false);
  const anchor = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<CSSProperties>({ top: 0, left: 0, width: 340, maxHeight: 360 });
  useEffect(() => {
    if (!open) return;
    const closeOutside = (e: PointerEvent) => {
      if (!anchor.current?.contains(e.target as Node) && !panel.current?.contains(e.target as Node)) setOpen(false);
    };
    const closeEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.stopPropagation(); setOpen(false); anchor.current?.focus(); }
    };
    const close = () => setOpen(false);
    document.addEventListener('pointerdown', closeOutside);
    document.addEventListener('keydown', closeEscape, true);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('pointerdown', closeOutside);
      document.removeEventListener('keydown', closeEscape, true);
      window.removeEventListener('resize', close);
    };
  }, [open]);
  function toggle() {
    const rect = anchor.current?.getBoundingClientRect();
    if (rect) {
      const width = Math.min(360, window.innerWidth - 16);
      const below = window.innerHeight - rect.bottom - 12;
      const above = rect.top - 12;
      const downward = below >= Math.min(300, above);
      const maxHeight = Math.max(80, Math.min(360, downward ? below : above));
      setPosition({ width, left: Math.max(8, Math.min(rect.right - width, window.innerWidth - width - 8)),
        ...(downward ? { top: rect.bottom + 4 } : { bottom: window.innerHeight - rect.top + 4 }), maxHeight });
    }
    setOpen(!open);
  }
  const [status, setStatus] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (status === null) return;
    const timer = window.setTimeout(() => setStatus(null), 2500);
    return () => window.clearTimeout(timer);
  }, [status]);
  if ((!permissions.isSuperAdmin && !permissions.can('visit.card.messengers')) || rows.length === 0) return null;

  async function copy(url: string) {
    try {
      await navigator.clipboard.writeText(url);
      setStatus('obrashcheniya.links.copied');
    } catch {
      setStatus('obrashcheniya.links.copy_failed');
    }
  }
  async function sms(row: NashMessendzher) {
    if (busy || !row.can_sms || (inquiryId === undefined && onSms === undefined)) return;
    setBusy(true);
    setStatus(null);
    try {
      const intent = crypto.randomUUID();
      const result = await (onSms ? onSms(row, intent) : otpravitSsylkuSms(inquiryId!, row.channel_id, intent));
      setStatus(result.ok ? 'obrashcheniya.links.sms_sent' : 'obrashcheniya.links.sms_failed');
    } catch (error) {
      const code = (error as { response?: { data?: { code?: string } } }).response?.data?.code;
      setStatus(code === 'too_long' ? 'obrashcheniya.links.too_long' : 'obrashcheniya.links.sms_failed');
    } finally {
      setBusy(false);
    }
  }

  return <section className="ops-skin nashi-messendzhery" data-testid="nashi-messendzhery">
    <button type="button" ref={anchor} className="nashi-messendzhery-toggle" aria-expanded={open} aria-haspopup="dialog" onClick={toggle}>
      <span>{text('obrashcheniya.links.title')}</span><IconChevronUp size={14} style={{ transform: open ? undefined : 'rotate(180deg)' }} />
    </button>
    {open && createPortal(<div ref={panel} className="ops-skin nashi-messendzhery-panel" role="dialog" aria-label={text('obrashcheniya.links.title')} style={{ position: 'fixed', ...position }}>{rows.map(row => <div key={row.channel_id} className="nash-messendzher" data-channel={row.channel_id}>
      <ZnachokKanala kind={row.kind} color="var(--ops-primary)" razmer={18} />
      <span className="nash-messendzher-label">{row.label}</span>
      <span className="nash-messendzher-url">{row.short_url.replace(/^https:\/\//, '')}</span>
      <button type="button" className="nash-messendzher-copy" aria-label={`${text('obrashcheniya.links.copy')} · ${row.label}`} title={text('obrashcheniya.links.copy')} onClick={() => void copy(row.short_url)}><IconCopy size={15} /></button>
      {row.can_sms && <button type="button" disabled={busy} aria-label={`${text('obrashcheniya.links.sms')} · ${row.label}`} title={text('obrashcheniya.links.sms')} onClick={() => void sms(row)}><IconMessage size={15} /></button>}
    </div>)}
    {status !== null && <div className="nashi-messendzhery-status" role="status">{text(status)}</div>}
    </div>, document.body)}
  </section>;
}
